import Cors from 'cors';
// import { default as makeFetch } from 'make-fetch-happen';
import makeFetch from 'isomorphic-unfetch';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

const DEFAULT_TIMEOUT =
  Number.isInteger(publicRuntimeConfig.FETCH_TIMEOUT) ?
    publicRuntimeConfig.FETCH_TIMEOUT :
    Number.parseInt(publicRuntimeConfig.FETCH_TIMEOUT);

// Helper method to wait for a middleware to execute before continuing
// And to throw an error when an error happens in a middleware
function runMiddleware(req, res, fn) {
  return new Promise((resolve, reject) => {
    fn(req, res, (result) => {
      if (result instanceof Error) {
        return reject(result);
      }

      return resolve(result);
    });
  });
}

export function initMiddleware(middleware) {
  return (req, res) =>
    new Promise((resolve, reject) => {
      middleware(req, res, (result) => {
        if (result instanceof Error) {
          return reject(result);
        }
        return resolve(result);
      });
    });
}

export async function applyCors(req, res, options) {
  const cors = Cors(options);

  return await runMiddleware(req, res, cors);
}

export function send (res, data = {}, status = 200) {
  res.statusCode = status;
  try {
    res.setHeader('Content-Type', 'application/json');
  } catch (error) {
    // do nothing
  }
  res.end(JSON.stringify(data));
}

/**
 * Wrapper for isomorphic-unfetch with sane defaults for frontend
 *
 * @param {string} url Endpoint URL
 * @param {object} options Options to configure request
 * @returns Promise<any>
 */
export function fetch (url: string, opts): Promise<any> {
  const computedOpts = {
    timeout: DEFAULT_TIMEOUT,
    ...(opts || {}),
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(opts || {})?.headers,
    },
    retry: {
      retries: 3,
      factor: 3,
      minTimeout: 1 * 1000,
      maxTimeout: 10 * 1000,
      randomize: true,
      ...opts?.retry
    },
  };

  return makeFetch(url, computedOpts);
}

export default fetch;

export async function fetchJson (url, options) {
  try {
    const response = await fetch(url, options);
    const contentType = response.headers.get('content-type');

    let data;
    if (contentType && contentType.indexOf('application/json') !== -1) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (response.ok) {
      return data;
    }

    const error = new Error(response.statusText) as any;
    error.response = response;
    error.data = data;
    throw error;
  } catch (error) {
    if (!error?.data) {
      error.data = { message: error.message };
    }
    throw error;
  }
}

export async function postJSON (url, data = {}, options:any = {}) {
  data = typeof data === 'object' ? JSON.stringify(data) : data;

  try {
    const result = await fetchJson(url, {
      method: 'POST',
      body: data,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
    });

    return {
      ...result,
      data: result?.data || null,
      error: result.error || null
    };
  } catch (error) {
    return {
      data: null,
      error
    };
  }
}

export async function uploadFile (file, bucket, name) {
  const formData = new FormData();
  formData.append('type', 'file');
  formData.append('file', file);

  return fetch(`/api/upload/${bucket}/${name}`, {
    method: 'POST',
    body: formData
  }).then(
    response => {
      if (response.json) {
        return response.json()?.data;
      }

      return response.text
        ? response.text()
        : response?.data ||response || null;
    }
  );
}
